import { Box, Typography, Paper, Alert } from "@mui/material";
import { useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";

interface FileUploadProps {
  label: string;
  onFileProcessed?: (data: { codigo: string }[]) => void;
  onError?: (error: string) => void;
}

export const FileUpload = ({
  label,
  onFileProcessed,
  onError,
}: FileUploadProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetError = () => {
    setError(null);
    // Não dispare onError aqui
  };

  const handleFiles = useCallback(
    (files: FileList | null) => {
      resetError();
      if (!files || files.length === 0) return;

      const file = files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (!fileExtension || !['csv', 'txt', 'xlsx'].includes(fileExtension)) {
        const msg = "Apenas arquivos .csv, .txt ou .xlsx são permitidos.";
        setError(msg);
        if (onError) onError(msg);
        return;
      }

      if (fileExtension === 'xlsx') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json<string[]>(firstSheet, { header: 1 });

            if (jsonData.length === 0) {
              throw new Error("Arquivo Excel vazio.");
            }

            const firstColumn = jsonData.map((row: { toString: () => string; }[]) => row[0]?.toString().trim() || '');
            const hasHeader = firstColumn[0]?.toLowerCase().includes('cbo') || firstColumn[0]?.toLowerCase().includes('codigo');
            const startIndex = hasHeader ? 1 : 0;

            const codes = firstColumn.slice(startIndex).filter((line: string | any[]) => line.length > 0);
            const parsedData = codes.map((codigo: string) => {
              return { codigo };
            });

            if (parsedData.length === 0) {
              throw new Error("Nenhum código CBO encontrado no arquivo.");
            }

            if (onFileProcessed) {
              onFileProcessed(parsedData);
            }
          } catch (err: any) {
            const msg = err.message || "Erro ao processar o arquivo Excel.";
            setError(msg);
            if (onError) onError(msg);
          }
        };
        reader.onerror = () => {
          const msg = "Erro ao ler o arquivo Excel.";
          setError(msg);
          if (onError) onError(msg);
        };
        reader.readAsArrayBuffer(file);
      } else {
        // CSV ou TXT
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            let lines: string[];

            if (fileExtension === 'csv') {
              const firstLine = content.split('\n')[0];
              const delimiter = firstLine.includes(';') ? ';' : ',';
              lines = content
                .split('\n')
                .map(line => {
                  const cols = line.split(delimiter);
                  return cols[0]?.trim() || '';
                })
                .filter(line => line.length > 0);
            } else {
              // TXT: um código por linha - melhorado para lidar com quebras de linha
              lines = content
                .split("\n")
                .map((line) => line.trim())
                .filter((line) => line.length > 0)
                .map((line) => {
                  // Remove quebras de linha internas e espaços extras
                  return line.replace(/\s+/g, '').replace(/\r/g, '');
                })
                .filter((line) => line.length > 0);
            }

            const hasHeader = lines[0]?.toLowerCase().includes("cbo") || lines[0]?.toLowerCase().includes("codigo");
            const startIndex = hasHeader ? 1 : 0;

            const parsedData = lines.slice(startIndex).map((line) => {
              // Limpa o código removendo quebras de linha e espaços extras
              const codigo = line.trim().replace(/\s+/g, '').replace(/\r/g, '');
              return { codigo };
            }).filter(item => item.codigo.length > 0);

            if (parsedData.length === 0) {
              throw new Error("Nenhum código CBO encontrado no arquivo.");
            }

            if (onFileProcessed) {
              onFileProcessed(parsedData);
            }
          } catch (err: any) {
            const msg = err.message || "Erro ao processar o arquivo.";
            setError(msg);
            if (onError) onError(msg);
          }
        };
        reader.onerror = () => {
          const msg = "Erro ao ler o arquivo.";
          setError(msg);
          if (onError) onError(msg);
        };
        reader.readAsText(file, "UTF-8");
      }
    },
    [onFileProcessed, onError]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 4,
        textAlign: "center",
        cursor: "pointer",
        backgroundColor: dragActive ? "action.hover" : "background.paper",
        transition: "background-color 0.2s",
        border: error ? "2px solid" : undefined,
        borderColor: error ? "error.main" : undefined,
      }}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.txt,.xlsx"
        hidden
        onChange={handleChange}
        onClick={(e) => {
          (e.target as HTMLInputElement).value = "";
        }}
      />
      <Typography variant="body1" color="textSecondary">
        {label}
      </Typography>
      <Box sx={{ mt: 2, color: "text.secondary" }} />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};